import { useEffect, useRef, useState } from 'react';
import { useAtomValue } from 'jotai';
import { useTrackComponentEvent } from 'components/forms/TimePicker/hooks/useTrackComponentEvent';
import { FilterPreset, TrackingData } from '../types';
import { areBoatsLoadingAtom, filteredBoatsAtom, isRouterReadyAtom } from '../../Filter/filterStore';

const useFilterPresetClickTracking = () => {
  const trackComponentEvent = useTrackComponentEvent();

  const filteredBoats = useAtomValue(filteredBoatsAtom);
  const routerReady = useAtomValue(isRouterReadyAtom);
  const areBoatsLoading = useAtomValue(areBoatsLoadingAtom);

  const [pendingTrackingData, setPendingTrackingData] = useState<TrackingData | null>(null);
  const hasTrackedRef = useRef(false);

  const triggerTracking = (preset: FilterPreset, position: number) => {
    setPendingTrackingData({
      preset,
      position,
    });

    // Reset the tracking flag
    hasTrackedRef.current = false;
  };

  useEffect(() => {
    // Only proceed if we have pending tracking data and haven't tracked yet
    if (!pendingTrackingData || hasTrackedRef.current) return;

    // Only track when router is ready and boats have finished loading
    if (!routerReady || areBoatsLoading) return;

    const boats_displayed =
      filteredBoats?.map((boat, index) => ({
        boatId: boat.boat_public_id,
        position: index + 1,
      })) || [];

    trackComponentEvent({
      event_name: 'click',
      component_type: 'button',
      object: 'preconfigured_filter_tile',
      context: 'plp',
      object_properties: {
        text: pendingTrackingData.preset.label,
        id: pendingTrackingData.preset.id,
        position: pendingTrackingData.position, // 1-based indexing
        boats_displayed,
      },
    });

    // Mark as tracked and clear pending data
    hasTrackedRef.current = true;
    setPendingTrackingData(null);
  }, [pendingTrackingData, routerReady, areBoatsLoading, filteredBoats]);

  return {
    triggerTracking,
  };
};

export default useFilterPresetClickTracking;
